// src/components/AdminVerify.js
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const AdminVerify = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { adminId, email } = location.state || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!adminId) {
      toast.error('Invalid verification request');
      navigate('/admin/register');
      return;
    }

    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/admin/verify-code`,
        { adminId, verificationCode: code },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success('Account verified successfully! Please log in.');
        navigate('/admin/form/11');
      }
    } catch (err) {
      console.error('[AdminVerify] Error:', err);
      toast.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (!adminId) {
    return (
      <div style={containerStyle}>
        <div style={formBoxStyle}>
          <h2 style={titleStyle}>Invalid Request</h2>
          <p style={subtitleStyle}>Please register first</p>
          <button
            onClick={() => navigate('/admin/register')}
            style={buttonStyle}
          >
            Go to Registration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={formBoxStyle}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>📧</div>
          <h2 style={titleStyle}>Verify Your Account</h2>
          <p style={subtitleStyle}>
            We've sent a 6-digit code to<br />
            <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={fieldGroupStyle}>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              style={codeInputStyle}
              maxLength={6}
              disabled={loading}
              autoFocus
            />
            <small style={hintStyle}>Enter the 6-digit code from your email</small>
            <small style={{ ...hintStyle, marginTop: '4px' }}>Code expires in 15 minutes</small>
          </div>

          <button
            type="submit"
            style={{
              ...buttonStyle,
              opacity: loading || code.length !== 6 ? 0.6 : 1,
              cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer'
            }}
            disabled={loading || code.length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify Account'}
          </button>
        </form>

        <div style={linkBoxStyle}>
          Didn't receive the code?{' '}
          <a href="/admin/register" style={linkStyle}>
            Register again
          </a>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '20px'
};

const formBoxStyle = {
  background: 'white',
  borderRadius: '12px',
  padding: '40px',
  maxWidth: '450px',
  width: '100%',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

const titleStyle = {
  margin: '0 0 8px 0',
  fontSize: '28px',
  fontWeight: '700',
  color: '#1f2937'
};

const subtitleStyle = {
  margin: '0',
  fontSize: '14px',
  color: '#6b7280',
  lineHeight: '1.6'
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  marginTop: '30px'
};

const fieldGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'center'
};

const codeInputStyle = {
  padding: '16px',
  border: '2px solid #d1d5db',
  borderRadius: '12px',
  fontSize: '32px',
  fontWeight: '700',
  textAlign: 'center',
  letterSpacing: '12px',
  outline: 'none',
  width: '100%',
  maxWidth: '280px',
  transition: 'border-color 0.2s',
  fontFamily: 'monospace'
};

const hintStyle = {
  fontSize: '12px',
  color: '#6b7280',
  textAlign: 'center'
};

const buttonStyle = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  border: 'none',
  padding: '14px 24px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: '600',
  marginTop: '8px',
  cursor: 'pointer'
};

const linkBoxStyle = {
  marginTop: '24px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#6b7280'
};

const linkStyle = {
  color: '#667eea',
  fontWeight: '600',
  textDecoration: 'none'
};

export default AdminVerify;